import React, { useState, useEffect, useRef } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import useIsMobile, { useMaxWidth } from '../../hooks/useIsMobile';
import {
  BarChartLine,
  ChatQuoteFill,
  JournalText,
  ListCheck,
  Lock,
  CheckCircleFill,
  XCircle,
  ShieldLock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowDown,
  Eye,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import VideoPlayer from '../shared/VideoPlayer';
import TypingAnimation from '../shared/TypingAnimation';
import { MarketingHeadlineAccent } from '../shared/MarketingHeadlineAccent';
import { MarketingResponsiveHeadline } from '../shared/MarketingResponsiveHeadline';
import ExpandableItem from '../landing/ExpandableItem';
import FeatureTabTray from '../shared/FeatureTabTray';
import { useConnectTradeLaunched } from '../../contexts/FeatureFlagsContext';
import PricingBrokerSupportTable from '../billing/PricingBrokerSupportTable';
import { getHomeStartPathBrokersForDisplay } from '../../data/shared/connectTradeBrokers';
import { LandingAppendixIcon, PrecisionAIIcon, TradingRulesShieldIcon, TradingWorkflowIcon } from '../landing/LandingAppendixIcons';
import analyticsService from '../../services/analyticsService';
import MockCandlestickBackdrop from '../StockChart/MockCandlestickBackdrop';

/** Accent word for a headline/title line; `headlineAccent` / `titleAccent` may be a string or per-line array. */
function resolveMarketingAccent(data, index = 0) {
  const raw = data?.headlineAccent ?? data?.titleAccent ?? data?.eyebrowAccent;
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw[index] ?? raw[raw.length - 1];
  return raw;
}

function resolveClosingLineAccent(data, index) {
  const raw = data?.closingLineAccents ?? data?.closingLineAccent;
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw[index];
  return index === 0 ? raw : undefined;
}

/**
 * Renders a visual (video or image) from config.
 * Used by hero, problemUsp, benefits, features.
 */
export const renderMedia = (visual, opts = {}) => {
  if (!visual?.src) return null;
  const { fill = false, onImageClick, analyticsContext } = opts;
  const autoplay = opts.autoplay ?? visual.autoplay ?? true;
  const loop = opts.loop ?? visual.loop ?? true;
  const muted = opts.muted ?? visual.muted ?? true;
  const controls = opts.controls ?? visual.controls ?? false;
  const playWhenVisible = opts.playWhenVisible ?? visual.playWhenVisible ?? false;
  const videoLabel = opts.videoLabel ?? visual.videoLabel;
  const posterPreview = opts.posterPreview ?? visual.posterPreview ?? false;
  if (visual.type === 'video') {
    return (
      <VideoPlayer
        src={visual.src}
        poster={visual.poster}
        alt={visual.alt}
        inline={true}
        autoplay={autoplay}
        playWhenVisible={playWhenVisible}
        loop={loop}
        muted={muted}
        controls={controls}
        fill={fill}
        videoLabel={videoLabel}
        analyticsContext={analyticsContext}
        posterPreviewOnMobile={opts.posterPreviewOnMobile ?? visual.posterPreviewOnMobile ?? false}
        posterPreview={posterPreview}
      />
    );
  }
  return (
    <img
      src={visual.src}
      alt={visual.alt || ''}
            onClick={onImageClick ? (e) => {
              e.stopPropagation();
              onImageClick(visual.src, visual.alt || '');
            } : undefined}
      style={onImageClick ? { cursor: 'pointer' } : undefined}
    />
  );
};

/**
 * Supported brokers — same Track/Trade table as pricing, with TradeStation promo strip.
 */
export function SupportedBrokersBlock({ data, landingConfigId }) {
  const connectTradeLaunched = useConnectTradeLaunched();

  return (
    <div className="landing-supported-brokers-block">
      <PricingBrokerSupportTable
        connectTradeLaunched={connectTradeLaunched}
        landingPageId={landingConfigId || 'home'}
        embedded
        title={data?.title || 'Supported Brokers'}
        titleAccent={data?.titleAccent || 'Brokers'}
        waitlistNote={data?.waitlistNote}
        waitlistCopy={data?.waitlistCopy || 'Get notified when we add support for your broker.'}
      />
    </div>
  );
}

/**
 * Counts from 0 to `target` (numeric string) when `isVisible` becomes true.
 * Non-numeric targets render as-is immediately.
 */
function CountUp({ target, isVisible, delay = 0 }) {
  const numTarget = parseInt(target, 10);
  const isNumeric = !isNaN(numTarget);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible || !isNumeric) return;
    let raf;
    const timeout = setTimeout(() => {
      const duration = 1300;
      const start = performance.now();
      const tick = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * numTarget));
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [isVisible, numTarget, isNumeric, delay]);

  return <>{isNumeric ? count : target}</>;
}

/** Chat bubble list with staggered slide-up on scroll-into-view. */
function SocialProofCardList({ quotes, cardsClassName, scrollReveal }) {
  const { ref, isVisible } = useScrollReveal(
    scrollReveal ?? { threshold: 0.05, rootMargin: '0px 0px -8% 0px' }
  );
  const revealedClass = isVisible ? ' landing-social-proof-banner-cards--revealed' : '';

  return (
    <ul className={`${cardsClassName}${revealedClass}`} ref={ref}>
      {quotes.map((q, i) => (
        <li
          key={q.id || i}
          className="landing-social-proof-banner-card"
          style={{ '--card-index': i }}
        >
          <figure className="landing-social-proof-banner-card-inner">
            <div className="landing-social-proof-banner-card-row">
              <span className="landing-social-proof-banner-quote-mark" aria-hidden="true">
                <ChatQuoteFill />
              </span>
              <blockquote className="landing-social-proof-banner-quote">{q.text}</blockquote>
            </div>
            {q.subtext && (
              <figcaption className="landing-social-proof-banner-attribution">
                <em>-- {q.subtext}</em>
              </figcaption>
            )}
          </figure>
        </li>
      ))}
    </ul>
  );
}

/**
 * Quote cards (landing strip after hero, or embedded e.g. home page why-choose column).
 * data: { eyebrow?, title?, quotes: [{ id?, text, subtext? }], ariaLabel? }
 * opts.variant: 'strip' (default) = full-width section + optional heading; 'embedded' = stacked list, no heading (use under an existing H2).
 * opts.headingIdSuffix: disambiguate aria ids when multiple instances share a page.
 * opts.sectionClassName: extra class on the outer strip section.
 * opts.scrollReveal: optional { threshold, rootMargin } for IntersectionObserver (e.g. /home).
 */
export function SocialProofBannerSection(data, opts = {}) {
  const {
    variant = 'strip',
    headingIdSuffix = '',
    sectionClassName = '',
    scrollReveal,
  } = opts;
  const quotes = data?.quotes;
  if (!quotes?.length) return null;
  const headingId = `landing-social-proof-banner-heading${headingIdSuffix}`;
  const hasHeading = variant === 'strip' && !!(data.eyebrow || data.title);
  const cardsClassName =
    variant === 'embedded'
      ? 'landing-social-proof-banner-cards landing-social-proof-banner-cards--embedded'
      : 'landing-social-proof-banner-cards';

  const list = (
    <SocialProofCardList quotes={quotes} cardsClassName={cardsClassName} scrollReveal={scrollReveal} />
  );

  if (variant === 'embedded') {
    return (
      <div
        className={`landing-social-proof-embedded${sectionClassName ? ` ${sectionClassName}` : ''}`}
        role="group"
        aria-label={data.ariaLabel || 'Trader feedback'}
      >
        {list}
        {data.disclaimer && (
          <p className="landing-social-proof-banner-disclaimer">{data.disclaimer}</p>
        )}
      </div>
    );
  }

  return (
    <section
      className={`landing-social-proof-banner${sectionClassName ? ` ${sectionClassName}` : ''}`}
      aria-labelledby={hasHeading ? headingId : undefined}
      aria-label={hasHeading ? undefined : data.ariaLabel || 'Trader feedback'}
    >
      <div className="landing-social-proof-banner-inner">
        {(data.eyebrow || data.title) && (
          <header className="landing-social-proof-banner-header">
            {data.eyebrow && data.title && (
              <p className="landing-social-proof-banner-eyebrow">
                <MarketingHeadlineAccent text={data.eyebrow} accent={resolveMarketingAccent(data)} />
              </p>
            )}
            {data.eyebrow && !data.title && (
              <h2
                className="landing-social-proof-banner-eyebrow landing-social-proof-banner-eyebrow--heading"
                id={headingId}
              >
                <MarketingHeadlineAccent text={data.eyebrow} accent={resolveMarketingAccent(data)} />
              </h2>
            )}
            {data.title && (
              <h2 className="landing-social-proof-banner-title" id={headingId}>
                <MarketingHeadlineAccent text={data.title} accent={resolveMarketingAccent(data)} />
              </h2>
            )}
          </header>
        )}
        {list}
        {data.disclaimer && (
          <p className="landing-social-proof-banner-disclaimer">{data.disclaimer}</p>
        )}
      </div>
    </section>
  );
}

/**
 * Contrasting CTA usage stats row (also used under Home “Why choose”).
 * usageSignals: { eyebrow?, footnote?, items: [{ id?, value, label }] }
 */
export function UsageSignalsBlock({ usageSignals, className = '', scrollRevealOptions } = {}) {
  const { ref, isVisible } = useScrollReveal(
    scrollRevealOptions ?? { threshold: 0.06, rootMargin: '0px 0px -8% 0px' }
  );
  if (!usageSignals?.items?.length) return null;
  return (
    <div ref={ref} className={`landing-cta-contrasting-usage${className ? ` ${className}` : ''}`}>
      {usageSignals.eyebrow && (
        <p className="landing-cta-contrasting-usage-eyebrow">{usageSignals.eyebrow}</p>
      )}
      <ul className="landing-cta-contrasting-usage-grid">
        {usageSignals.items.map((item, i) => (
          <li key={item.id || i} className="landing-cta-contrasting-usage-card">
            <span className="landing-cta-contrasting-usage-value">
              <CountUp target={item.value} isVisible={isVisible} delay={i * 160} />
            </span>
            <span className="landing-cta-contrasting-usage-label">{item.label}</span>
          </li>
        ))}
      </ul>
      {usageSignals.footnote && (
        <p className="landing-cta-contrasting-usage-footnote">{usageSignals.footnote}</p>
      )}
    </div>
  );
}

const BENEFITS_CAROUSEL_UI = {
  root: 'benefits-carousel',
  slide: 'benefits-carousel-slide',
  slideFading: 'benefits-carousel-slide--fading',
  dots: 'benefits-carousel-dots',
  dot: 'benefits-carousel-dot',
  dotActive: 'benefits-carousel-dot--active',
};

const HERO_CAROUSEL_UI = {
  root: 'landing-hero-carousel',
  slide: 'landing-hero-carousel-slide',
  slideFading: 'landing-hero-carousel-slide--fading',
  dots: 'landing-hero-carousel-dots',
  dot: 'landing-hero-carousel-dot',
  dotActive: 'landing-hero-carousel-dot--active',
};

/**
 * Auto-rotating media carousel (images or videos). Fixed-height slide box + object-fit: contain
 * keeps layout stable when assets have different aspect ratios.
 */
function LandingRotatingMediaCarousel({
  visuals,
  intervalMs = 7000,
  onImageClick,
  ui = BENEFITS_CAROUSEL_UI,
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);

  const advance = (next) => {
    setFading(true);
    setTimeout(() => {
      setActiveIdx(next);
      setFading(false);
    }, 350);
  };

  useEffect(() => {
    if (visuals.length < 2) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((cur) => {
        const next = (cur + 1) % visuals.length;
        advance(next);
        return cur;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [visuals.length, intervalMs]);

  const goTo = (idx) => {
    clearInterval(timerRef.current);
    advance(idx);
    timerRef.current = setInterval(() => {
      setActiveIdx((cur) => {
        const next = (cur + 1) % visuals.length;
        advance(next);
        return cur;
      });
    }, intervalMs);
  };

  const v = visuals[activeIdx];

  return (
    <div className={ui.root}>
      <div className={`${ui.slide} ${fading ? ui.slideFading : ''}`}>
        {v.type === 'video' ? (
          renderMedia(v, { onImageClick })
        ) : (
          <img
            src={v.src}
            alt={v.alt || ''}
            onClick={onImageClick ? () => onImageClick(v.src, v.alt || '') : undefined}
            style={onImageClick ? { cursor: 'pointer' } : undefined}
          />
        )}
      </div>
      {visuals.length > 1 && (
        <div className={ui.dots}>
          {visuals.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`${ui.dot} ${i === activeIdx ? ui.dotActive : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Show slide ${i + 1} of ${visuals.length}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BenefitsCarousel({ visuals, intervalMs = 7000, onImageClick }) {
  return (
    <LandingRotatingMediaCarousel
      visuals={visuals}
      intervalMs={intervalMs}
      onImageClick={onImageClick}
      ui={BENEFITS_CAROUSEL_UI}
    />
  );
}

/**
 * Section renderers - each receives (sectionData, { ctaUrl, ctaLabel, handleCtaClick }).
 * Template iterates sectionOrder and calls the matching renderer.
 * Add new section types here to extend the template.
 */
function ProblemUspSection({ data, openImageViewer }) {
  const isMobile = useIsMobile(767, 900);
  const visual = isMobile && data.mobileVisual?.src ? data.mobileVisual : data.visual;
  const [activeFlowCard, setActiveFlowCard] = useState(null);
  const flowCards = Array.isArray(data.flowCards) ? data.flowCards : [];
  const showFlowCards = flowCards.length > 0;
  const showTaglineBeforeMedia = data.tagline && data.taglinePlacement !== 'afterMedia';
  const flowIconById = {
    'set-trading-rules': ListCheck,
    'rule-enforcement': Lock,
    'automatic-journaling': JournalText,
    'review-performance': BarChartLine,
  };

  return (
    <section className={`landing-section landing-problem-usp ${data.background === 'accent' ? 'landing-usp-accent' : ''}`}>
      <div className="landing-problem-stack">
        {data.headline && (
          <h2 className={`landing-problem-headline${showFlowCards ? ' landing-problem-headline--flow' : ''}`}>
            {Array.isArray(data.headline)
              ? data.headline.map((line, i) => (
                  <span key={i} className="landing-problem-headline-line">
                    <MarketingHeadlineAccent text={line} accent={data.headlineAccent} />
                  </span>
                ))
              : (
                  <MarketingHeadlineAccent text={data.headline} accent={data.headlineAccent} />
                )}
          </h2>
        )}
        {(data.paragraphs?.length > 0 || showTaglineBeforeMedia) && (
          <div className="landing-problem-intro">
            {data.paragraphs?.map((p, i) => (
              <p key={i} className="landing-usp-text" dangerouslySetInnerHTML={{ __html: p }} />
            ))}
            {showTaglineBeforeMedia && (
              <p className="landing-usp-tagline landing-usp-tagline--before-media">{data.tagline}</p>
            )}
          </div>
        )}
        {showFlowCards && data.flowTitle && (
          <h3 className="landing-problem-flow-title">{data.flowTitle}</h3>
        )}
        {showFlowCards ? (
          <div className="landing-problem-flow" role="list" aria-label={data.flowCardsAriaLabel || 'Execution flow'}>
            {flowCards.map((card, index) => {
              const cardId = card.id || `flow-${index}`;
              const isFlipped = activeFlowCard === cardId;
              const Icon = flowIconById[card.id] || ListCheck;
              return (
                <button
                  key={cardId}
                  type="button"
                  className={`landing-problem-flow-card ${isFlipped ? 'is-flipped' : ''}`}
                  role="listitem"
                  onClick={() => setActiveFlowCard((prev) => (prev === cardId ? null : cardId))}
                  onMouseLeave={
                    isMobile
                      ? undefined
                      : () => setActiveFlowCard((prev) => (prev === cardId ? null : prev))
                  }
                  aria-pressed={isFlipped}
                >
                  <span className="landing-problem-flow-card-face landing-problem-flow-card-face--front">
                    <span className="landing-problem-flow-card-corner" aria-hidden="true" />
                    <span className="landing-problem-flow-card-step">Step {index + 1}</span>
                    <span className="landing-problem-flow-card-main">
                      <span className="landing-problem-flow-card-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span className="landing-problem-flow-card-title">{card.title}</span>
                    </span>
                  </span>
                  <span className="landing-problem-flow-card-face landing-problem-flow-card-face--back">
                    {card.description}
                  </span>
                </button>
              );
            })}
          </div>
        ) : visual?.src ? (
          <div className="landing-problem-media-full">
            {renderMedia(visual, { onImageClick: openImageViewer })}
          </div>
        ) : null}
        {showFlowCards && data.mobileTapHint && (
          <p className="landing-problem-flow-hint">{data.mobileTapHint}</p>
        )}
        {data.tagline && (
          <p
            className={`landing-usp-tagline landing-usp-tagline--after-media${
              showFlowCards ? ' landing-usp-tagline--after-flow' : ''
            }`}
          >
            {data.tagline}
          </p>
        )}
        {data.quote?.leadLines?.length >= 2 && data.quote?.closing ? (
          <div className="landing-problem-quote">
            <p className="landing-problem-quote-lead">
              {data.quote.leadLines[0]}
              <br />
              {data.quote.leadLines[1]}
            </p>
            <hr className="landing-problem-quote-rule" />
            <p className="landing-problem-quote-closing">{data.quote.closing}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Single FAQ row — collapsed by default; chevron toggles, not a button element. */
function FaqAccordionItem({ item, answer }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const panelId = `landing-faq-panel-${item.id}`;

  useEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;
    if (open) node.removeAttribute('inert');
    else node.setAttribute('inert', '');
    return undefined;
  }, [open]);

  const toggle = () => setOpen((prev) => !prev);

  return (
    <article
      className={`landing-faq-card landing-quick-answer-card${open ? ' landing-faq-card--open' : ''}`}
      role="listitem"
    >
      <h3
        id={`${panelId}-label`}
        className="landing-faq-card-header landing-quick-answer-question"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        tabIndex={0}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="landing-faq-question-text">{item.question}</span>
        <span className={`landing-faq-chevron${open ? ' landing-faq-chevron--open' : ''}`} aria-hidden="true">
          <ChevronDown size={18} />
        </span>
      </h3>
      <div
        id={panelId}
        ref={panelRef}
        className={`landing-desk-drawer${open ? ' landing-desk-drawer--open' : ''}`}
        role="region"
        aria-labelledby={`${panelId}-label`}
        aria-hidden={!open}
      >
        <div className="landing-desk-drawer__inner">
          <div className="landing-faq-answer-wrap">
            <p className="landing-quick-answer-answer">
              {answer}
              {item.answerLink?.href && (
                <>
                  {' '}
                  <Link to={item.answerLink.href} className="landing-quick-answer-link">
                    {item.answerLink.text || 'Learn more'}
                  </Link>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

/** Orientation Q&A cards — used for bottom-of-page FAQ (and legacy quickAnswers). */
function QuickAnswersSection({ data, variant = 'quick' }) {
  const connectTradeLaunched = useConnectTradeLaunched();
  const items = data.items || [];
  if (!items.length) return null;

  const isFaq = variant === 'faq';
  const sectionClass = isFaq ? 'landing-faq' : 'landing-quick-answers';
  const gridClass = isFaq ? 'landing-faq-grid' : 'landing-quick-answers-grid';

  return (
    <section id={isFaq ? 'landing-faq' : undefined} className={`landing-section ${sectionClass}`}>
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
        {data.title && (
          <h2 className="landing-section-title">
            <MarketingResponsiveHeadline
              text={data.title}
              mobileLines={data.titleMobileLines}
              accent={resolveMarketingAccent(data)}
            />
          </h2>
        )}
        <div className={gridClass} role="list">
          {items.map((item) => {
            const answer = connectTradeLaunched && item.answerWhenLaunched ? item.answerWhenLaunched : item.answer;
            if (isFaq) {
              return <FaqAccordionItem key={item.id} item={item} answer={answer} />;
            }
            return (
              <article key={item.id} className="landing-quick-answer-card" role="listitem">
                <h3 className="landing-quick-answer-question">{item.question}</h3>
                <p className="landing-quick-answer-answer">
                  {answer}
                  {item.answerLink?.href && (
                    <>
                      {' '}
                      <Link to={item.answerLink.href} className="landing-quick-answer-link">
                        {item.answerLink.text || 'Learn more'}
                      </Link>
                    </>
                  )}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Wavy-line circle for “partial support” cells in the comparison table. */
function ComparisonPartialIcon({ className, title = 'Partial' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" role="img" aria-label={title}>
      <path
        fill="currentColor"
        d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.65 8.2c.62-1 1.2-1 1.78 0 .58 1 1.16 1 1.74 0 .58-1 1.16-1 1.78 0 .62 1 1.2 1 1.83 0"
      />
    </svg>
  );
}

/** Named comparison table vs. journal apps and lockout scripts — the aggressive, specific, honest format. */
function ComparisonTableSection({ data }) {
  const isMobile = useIsMobile(768, 1024);
  const columns = data.columns || [];
  const rows = data.rows || [];
  if (!columns.length || !rows.length) return null;

  const displayColumns = isMobile
    ? [...columns.filter((col) => col.highlight), ...columns.filter((col) => !col.highlight)]
    : columns;

  const renderCell = (raw) => {
    // Cells may be a plain value, or { value, hint } for a short caption under the icon
    // (e.g. clarifying what "Partial" means for that specific row).
    const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw.value : raw;
    const hint = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw.hint : null;

    let icon;
    if (value === true) icon = <CheckCircleFill className="landing-comparison-icon landing-comparison-icon--yes" aria-label="Yes" />;
    else if (value === false) icon = <XCircle className="landing-comparison-icon landing-comparison-icon--no" aria-label="No" />;
    else if (typeof value === 'string' && value.toLowerCase() === 'partial') {
      icon = <ComparisonPartialIcon className="landing-comparison-icon landing-comparison-icon--partial" />;
    } else {
      return <span className="landing-comparison-text">{value}</span>;
    }

    if (!hint) return icon;
    return (
      <span className="landing-comparison-cell-stack">
        {icon}
        <span className="landing-comparison-cell-hint">{hint}</span>
      </span>
    );
  };

  return (
    <section className="landing-section landing-comparison-table">
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
        {data.title && (
          <h2 className="landing-section-title">
            <MarketingResponsiveHeadline
              text={data.title}
              mobileLines={data.titleMobileLines}
              accent={resolveMarketingAccent(data)}
            />
          </h2>
        )}
        {data.intro && <p className="landing-comparison-intro">{data.intro}</p>}
        <div className="landing-comparison-table-scroll">
          <table className="landing-comparison-table-grid">
            <thead>
              <tr>
                <th scope="col" className="landing-comparison-feature-col" />
                {displayColumns.map((col) => (
                  <th
                    key={col.id}
                    scope="col"
                    className={`landing-comparison-col-head${col.highlight ? ' landing-comparison-col-head--highlight' : ''}`}
                  >
                    <span className="landing-comparison-col-label">{col.label}</span>
                    {col.sublabel && <span className="landing-comparison-col-sublabel">{col.sublabel}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.feature || i}>
                  <th scope="row" className="landing-comparison-feature">
                    {row.feature}
                    {row.note && <span className="landing-comparison-note">{row.note}</span>}
                  </th>
                  {displayColumns.map((col) => (
                    <td
                      key={col.id}
                      className={`landing-comparison-cell${col.highlight ? ' landing-comparison-cell--highlight' : ''}`}
                    >
                      {renderCell(row[col.id])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.footnote && <p className="landing-comparison-footnote">{data.footnote}</p>}
      </div>
    </section>
  );
}

/** Plain-language "what can you do with my account access" — out-explains badge-only compliance sections. */
function TrustExplainerSection({ data }) {
  const points = data.points || [];
  if (!points.length) return null;

  const linkClassName = 'landing-trust-explainer-link';
  const linkLabel = data.linkText || 'Learn more';

  return (
    <section
      id={data.sectionId || undefined}
      className="landing-section landing-trust-explainer"
    >
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow landing-trust-explainer-eyebrow">{data.eyebrow}</p>}
        <div className="landing-trust-explainer-inner">
          <div className="landing-trust-explainer-header">
            <div className="landing-trust-explainer-icon" aria-hidden="true">
              <ShieldLock />
            </div>
            {data.title && (
              <h2 className="landing-section-title landing-trust-explainer-title">
                <MarketingHeadlineAccent text={data.title} accent={resolveMarketingAccent(data)} />
              </h2>
            )}
          </div>
          <ul className="landing-trust-explainer-points">
            {points.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>
          {data.linkHref && (
            data.linkHref.startsWith('#') ? (
              <a href={data.linkHref} className={linkClassName}>
                {linkLabel}
              </a>
            ) : (
              <Link to={data.linkHref} className={linkClassName}>
                {linkLabel}
              </Link>
            )
          )}
        </div>
      </div>
    </section>
  );
}

const SCREENSHOTS_CAROUSEL_UI = {
  root: 'landing-screenshots-carousel',
  slide: 'landing-screenshots-carousel-slide',
  slideFading: 'landing-screenshots-carousel-slide--fading',
  dots: 'landing-screenshots-carousel-dots',
  dot: 'landing-screenshots-carousel-dot',
  dotActive: 'landing-screenshots-carousel-dot--active',
};

function PainVisualCollage({ visuals, openImageViewer }) {
  const defaultFocused = Math.max(0, visuals.length - 1);
  const [focusedIndex, setFocusedIndex] = useState(defaultFocused);
  const focused = visuals[focusedIndex] || visuals[defaultFocused];
  const caption = focused?.caption || '';

  return (
    <div className="landing-pain-visual landing-pain-visual--collage">
      <div className="landing-pain-collage-stack" role="group" aria-label="Product screenshots">
        {visuals.map((visual, index) => {
          const layer = index === 0 ? 'back' : 'front';
          const isFocused = index === focusedIndex;
          return (
            <img
              key={`${visual.src}-${layer}`}
              src={visual.src}
              alt={visual.alt || ''}
              className={[
                'landing-pain-visual-image',
                `landing-pain-visual-image--collage-${layer}`,
                isFocused ? 'is-focused' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              loading="lazy"
              decoding="async"
              tabIndex={0}
              role={openImageViewer ? 'button' : undefined}
              aria-pressed={isFocused}
              onMouseEnter={() => setFocusedIndex(index)}
              onFocus={() => setFocusedIndex(index)}
              onClick={
                openImageViewer
                  ? () => openImageViewer(visual.src, visual.alt || '')
                  : () => setFocusedIndex(index)
              }
              onKeyDown={
                openImageViewer
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openImageViewer(visual.src, visual.alt || '');
                      }
                    }
                  : undefined
              }
              style={{ cursor: 'pointer' }}
            />
          );
        })}
      </div>
      <p className="landing-pain-collage-caption" aria-live="polite">
        {caption}
      </p>
    </div>
  );
}

function MechanismShowcaseImage({ image, onImageClick }) {
  if (!image?.src && !image?.mobileSrc) return null;
  const alt = image.alt || '';
  const desktopSrc = image.src;
  const mobileSrc = image.mobileSrc || image.src;
  const clickProps = (src) =>
    onImageClick
      ? {
          onClick: () => onImageClick(src, alt),
          style: { cursor: 'pointer' },
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onImageClick(src, alt);
            }
          },
        }
      : {};

  return (
    <div className="landing-mechanism-showcase">
      {desktopSrc && (
        <img
          className={`landing-mechanism-showcase-img landing-mechanism-showcase-img--desktop${
            onImageClick ? ' landing-desk-shot' : ''
          }`}
          src={desktopSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          {...clickProps(desktopSrc)}
        />
      )}
      {mobileSrc && (
        <img
          className={`landing-mechanism-showcase-img landing-mechanism-showcase-img--mobile${
            onImageClick ? ' landing-desk-shot' : ''
          }`}
          src={mobileSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          {...clickProps(mobileSrc)}
        />
      )}
    </div>
  );
}

/** Track when a landing expandable opens (not on collapse). */
function trackLandingExpandableExpanded({ section, itemId, itemTitle }) {
  analyticsService.trackTradingEvent?.('landing_expandable_expanded', {
    section,
    item_id: itemId || undefined,
    item_title: itemTitle || undefined,
  });
}

function MechanismPillarCard({
  pillar,
  index,
  Icon,
  iconClassName,
  visual,
  openImageViewer,
  emphasized,
  expanded,
  onToggle,
}) {
  const isStatic = Boolean(pillar.static || pillar.expandable === false);
  const canExpand = Boolean(pillar.description) && !isStatic;

  const toggleExpanded = () => {
    if (!canExpand) return;
    const next = !expanded;
    if (next) {
      trackLandingExpandableExpanded({
        section: 'mechanism',
        itemId: pillar.id,
        itemTitle: pillar.title,
      });
    }
    onToggle?.(pillar.id, next);
  };

  const onCardKeyDown = (event) => {
    if (!canExpand) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div className="landing-mechanism-pillar-reveal">
    <article
      className={`landing-mechanism-pillar${expanded || isStatic ? ' landing-mechanism-pillar--expanded' : ''}${
        emphasized ? ' landing-mechanism-pillar--emphasized' : ''
      }${canExpand ? ' landing-mechanism-pillar--expandable' : ''}${
        isStatic ? ' landing-mechanism-pillar--static' : ''
      }`}
      role={canExpand ? 'button' : 'listitem'}
      tabIndex={canExpand ? 0 : undefined}
      aria-expanded={canExpand ? expanded : undefined}
      aria-label={
        canExpand
          ? expanded
            ? `Collapse ${pillar.title}`
            : `Expand ${pillar.title}`
          : undefined
      }
      onClick={canExpand ? toggleExpanded : undefined}
      onKeyDown={canExpand ? onCardKeyDown : undefined}
    >
      <div className="landing-mechanism-pillar-visual">
        {visual?.type === 'placeholder' ? (
          <div
            className="landing-mechanism-placeholder"
            aria-label={pillar.title || visual.label || 'Feature visual'}
          >
            <Icon className={iconClassName} aria-hidden="true" />
          </div>
        ) : visual?.src ? (
          renderMedia(visual, { onImageClick: openImageViewer })
        ) : null}
      </div>
      <div className="landing-mechanism-pillar-copy">
        <span className="landing-mechanism-pillar-index">0{index + 1}</span>
        <div className="landing-mechanism-pillar-title-row">
          <h3 className="landing-mechanism-pillar-title">{pillar.title}</h3>
          {canExpand && (
            <span
              className={`landing-mechanism-pillar-expand${expanded ? ' landing-mechanism-pillar-expand--expanded' : ''}`}
              aria-hidden="true"
            >
              <ChevronDown size={18} aria-hidden="true" />
            </span>
          )}
        </div>
        {pillar.description && (
          <div
            className={`landing-desk-drawer${expanded && !isStatic ? ' landing-desk-drawer--open' : ''}${
              isStatic ? ' landing-desk-drawer--static landing-desk-drawer--open' : ''
            }`}
            aria-hidden={isStatic ? false : !expanded}
          >
            <div className="landing-desk-drawer__inner">
              <p className="landing-mechanism-pillar-description">{pillar.description}</p>
            </div>
          </div>
        )}
      </div>
    </article>
    </div>
  );
}

const MECHANISM_PILLAR_EMPHASIS_MS = [2600, 2600, 3800];

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => mediaQuery.removeEventListener('change', sync);
  }, []);

  return prefersReducedMotion;
}

function MechanismPillarsList({ pillars, pillarIcons, openImageViewer }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const pillarCount = pillars?.length ?? 0;
  const allStatic = pillars.every((pillar) => pillar.static || pillar.expandable === false);
  const shouldAutoEmphasize = !allStatic && pillarCount > 1 && !prefersReducedMotion;
  const defaultOpenId =
    pillars.find((pillar) => pillar.defaultExpanded)?.id ?? null;
  const [openId, setOpenId] = useState(defaultOpenId);

  useEffect(() => {
    if (!shouldAutoEmphasize || isPaused) return undefined;

    const delay = MECHANISM_PILLAR_EMPHASIS_MS[activeIdx] ?? MECHANISM_PILLAR_EMPHASIS_MS[0];
    const timerId = window.setTimeout(() => {
      setActiveIdx((current) => (current + 1) % pillarCount);
    }, delay);

    return () => window.clearTimeout(timerId);
  }, [activeIdx, isPaused, pillarCount, shouldAutoEmphasize]);

  const pauseAutoRotate = () => setIsPaused(true);

  const resumeAutoRotate = (event) => {
    const nextTarget = event.relatedTarget;
    const staysInside =
      nextTarget instanceof Node && event.currentTarget.contains(nextTarget);
    if (!staysInside) setIsPaused(false);
  };

  const handleToggle = (id, nextOpen) => {
    setOpenId(nextOpen ? id : null);
    pauseAutoRotate();
  };

  return (
    <div
      className={`landing-mechanism-pillars${isPaused ? ' landing-mechanism-pillars--paused' : ''}${
        allStatic ? ' landing-mechanism-pillars--static' : ''
      }`}
      role="list"
      onPointerEnter={shouldAutoEmphasize ? pauseAutoRotate : undefined}
      onPointerLeave={shouldAutoEmphasize ? resumeAutoRotate : undefined}
      onFocusCapture={shouldAutoEmphasize ? pauseAutoRotate : undefined}
      onBlurCapture={shouldAutoEmphasize ? resumeAutoRotate : undefined}
    >
      {pillars.map((pillar, index) => {
        const Icon = pillarIcons[pillar.id] || ListCheck;
        const iconClassName =
          pillar.id === 'rule-enforcement' || pillar.id === 'rules-setup-enforcement'
            ? 'landing-mechanism-placeholder-icon landing-mechanism-placeholder-icon--lock'
            : pillar.id === 'trading-workflow'
              ? 'landing-mechanism-placeholder-icon landing-mechanism-placeholder-icon--workflow'
              : 'landing-mechanism-placeholder-icon';

        return (
          <MechanismPillarCard
            key={pillar.id || index}
            pillar={pillar}
            index={index}
            Icon={Icon}
            iconClassName={iconClassName}
            visual={pillar.visual}
            openImageViewer={openImageViewer}
            emphasized={shouldAutoEmphasize && !isPaused && activeIdx === index}
            expanded={openId === pillar.id}
            onToggle={handleToggle}
          />
        );
      })}
    </div>
  );
}

function ScreenshotsCarouselSlideContent({ slide }) {
  const isMobile = useMaxWidth(767);
  const gallery = slide.gallery?.filter((item) => item?.src);

  // Mobile gallery slides (legacy trio) — only render on mobile viewports
  if (gallery?.length && isMobile && !slide.src) {
    return (
      <div className="landing-screenshots-carousel-mobile-single">
        <img
          src={slide.mobileSrc || gallery[0].src}
          alt={slide.alt || gallery[0].alt || ''}
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  if (gallery?.length && !isMobile && !slide.src) {
    return (
      <div className="landing-screenshots-carousel-mobile-trio" aria-hidden="false">
        {gallery.map((item) => (
          <img
            key={item.src}
            src={item.src}
            alt={item.alt || ''}
            loading="lazy"
            decoding="async"
          />
        ))}
      </div>
    );
  }

  const src = isMobile
    ? (slide.mobileSrc || slide.src || gallery?.[0]?.src)
    : (slide.src || slide.mobileSrc || gallery?.[0]?.src);
  const alt = slide.alt || gallery?.[0]?.alt || '';

  if (!src) return null;

  return (
    <img src={src} alt={alt} loading="lazy" decoding="async" />
  );
}

/**
 * Product-proof gallery: a handful of real screenshots (rules config, bracket orders, violation
 * analytics) with per-slide captions. Fixed-height slide box keeps the rest of the page from
 * shifting between differently-sized screenshots. Auto-rotates unless hovered, and supports
 * manual prev/next + dot navigation.
 */
function ScreenshotsCarouselSection({ data }) {
  const isMobile = useMaxWidth(767);
  const slides = (data.slides || []).filter((s) => {
    if (!s) return false;
    if (isMobile) return Boolean(s.mobileSrc || s.gallery?.length || s.src);
    // Desktop: only slides with a desktop screenshot (skip mobile-only gallery slides)
    return Boolean(s.src);
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const intervalMs = data.intervalMs ?? 6000;

  useEffect(() => {
    setActiveIdx(0);
  }, [isMobile, slides.length]);

  const advance = (next) => {
    setFading(true);
    setTimeout(() => {
      setActiveIdx(next);
      setFading(false);
    }, 350);
  };

  useEffect(() => {
    if (slides.length < 2 || isPaused) return undefined;
    timerRef.current = setInterval(() => {
      setActiveIdx((cur) => {
        const next = (cur + 1) % slides.length;
        advance(next);
        return cur;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [slides.length, intervalMs, isPaused]);

  if (!slides.length) return null;

  const goTo = (idx) => {
    clearInterval(timerRef.current);
    advance(idx);
  };

  const goToRelative = (delta) => {
    const next = (activeIdx + delta + slides.length) % slides.length;
    goTo(next);
  };

  const active = slides[activeIdx] || slides[0];
  if (!active) return null;

  return (
    <section className="landing-section landing-screenshots-section">
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
        {data.title && (
          <h2 className="landing-section-title">
            <MarketingHeadlineAccent text={data.title} accent={resolveMarketingAccent(data)} />
          </h2>
        )}
        <div
          className={SCREENSHOTS_CAROUSEL_UI.root}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="landing-screenshots-carousel-frame">
            {slides.length > 1 && (
              <button
                type="button"
                className="landing-screenshots-carousel-arrow landing-screenshots-carousel-arrow--prev"
                onClick={() => goToRelative(-1)}
                aria-label="Previous screenshot"
              >
                <ChevronLeft aria-hidden />
              </button>
            )}
            <div
              className={`${SCREENSHOTS_CAROUSEL_UI.slide} ${fading ? SCREENSHOTS_CAROUSEL_UI.slideFading : ''}${
                active.gallery?.length ? ' landing-screenshots-carousel-slide--mobile-gallery' : ''
              }`}
            >
              <ScreenshotsCarouselSlideContent slide={active} />
            </div>
            {slides.length > 1 && (
              <button
                type="button"
                className="landing-screenshots-carousel-arrow landing-screenshots-carousel-arrow--next"
                onClick={() => goToRelative(1)}
                aria-label="Next screenshot"
              >
                <ChevronRight aria-hidden />
              </button>
            )}
          </div>
          {(active.caption || active.label) && (
            <div className="landing-screenshots-caption">
              {active.label && <span className="landing-screenshots-caption-label">{active.label}</span>}
              {active.caption && <span className="landing-screenshots-caption-text">{active.caption}</span>}
            </div>
          )}
          {slides.length > 1 && (
            <div className={SCREENSHOTS_CAROUSEL_UI.dots}>
              {slides.map((s, i) => (
                <button
                  key={s.src || s.mobileSrc || s.gallery?.[0]?.src || s.label || i}
                  type="button"
                  className={`${SCREENSHOTS_CAROUSEL_UI.dot} ${i === activeIdx ? SCREENSHOTS_CAROUSEL_UI.dotActive : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={s.label ? `Show ${s.label}` : `Show slide ${i + 1} of ${slides.length}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const RULE_DEMO_MODE_ICONS = {
  track: Eye,
  enforce: ShieldLock,
};

function RuleDemoFeaturesLink({ data }) {
  if (!data?.featuresLinkHref) return null;
  return (
    <Link to={data.featuresLinkHref} className="landing-rule-demo-features-link">
      {data.featuresLinkText || 'See all supported rules'}
    </Link>
  );
}

function RuleDemoHowItWorksList({ callouts, openStepId, onToggleStep, idPrefix }) {
  if (!callouts.length) return null;
  return (
    <ol className="landing-rule-demo-how__steps">
      {callouts.map((item, index) => {
        const stepId = item.id || String(index);
        const open = openStepId === stepId;
        const panelId = `${idPrefix}-panel-${stepId}`;
        const buttonId = `${idPrefix}-btn-${stepId}`;
        const alert = item.id === 'no-override';
        return (
          <li key={stepId} className={`landing-rule-demo-how__step${open ? ' landing-rule-demo-how__step--open' : ''}`}>
            <h3 className="landing-rule-demo-how__heading">
              <button
                type="button"
                id={buttonId}
                className="landing-rule-demo-how__toggle"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => onToggleStep?.(stepId, item.title)}
              >
                <span className="landing-rule-demo-how__num" aria-hidden="true">
                  {index + 1}
                </span>
                <span
                  className={`landing-rule-demo-how__title${
                    alert ? ' landing-rule-demo-how__title--alert' : ''
                  }`}
                >
                  {item.title}
                </span>
                <ChevronDown
                  className={`landing-rule-demo-how__chevron${
                    open ? ' landing-rule-demo-label-chevron--expanded' : ''
                  }`}
                  size={16}
                  aria-hidden="true"
                />
              </button>
            </h3>
            {item.text ? (
              <div
                id={panelId}
                className={`landing-desk-drawer${open ? ' landing-desk-drawer--open' : ''}`}
                role="region"
                aria-labelledby={buttonId}
                aria-hidden={!open}
              >
                <div className="landing-desk-drawer__inner">
                  <p className="landing-rule-demo-how__text">{item.text}</p>
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function RuleDemoModesRow({ modeCards }) {
  if (!modeCards.length) return null;
  return (
    <ul className="landing-rule-demo-modes-row" aria-label="Rule modes">
      {modeCards.map((item) => {
        const ModeIcon = RULE_DEMO_MODE_ICONS[item.id];
        return (
          <li
            key={item.id}
            className={`landing-rule-demo-modes-row__item landing-rule-demo-modes-row__item--${item.id}`}
          >
            <span className="landing-rule-demo-modes-row__label">
              {ModeIcon ? (
                <ModeIcon className="landing-rule-demo-modes-row__icon" size={14} aria-hidden="true" />
              ) : null}
              {item.title}
            </span>
            {item.text ? <p className="landing-rule-demo-modes-row__text">{item.text}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}

function RuleDemoCalloutsStack({
  data,
  callouts,
  openStepId,
  onToggleStep,
  mobile = false,
}) {
  const list = (
    <RuleDemoHowItWorksList
      callouts={callouts}
      openStepId={openStepId}
      onToggleStep={onToggleStep}
      idPrefix={mobile ? 'rule-demo-mobile' : 'rule-demo-desk'}
    />
  );

  if (mobile) {
    return (
      <div className="landing-rule-demo-mobile-stack">
        {data.stackLabel && (
          <p className="landing-rule-demo-stack-label landing-rule-demo-stack-label--mobile">
            {data.stackLabel}
          </p>
        )}
        {list}
      </div>
    );
  }

  return (
    <div className="landing-rule-demo-callouts-stack landing-rule-demo-callouts-stack--simple">
      {data.stackLabel && (
        <p className="landing-rule-demo-stack-label">{data.stackLabel}</p>
      )}
      {list}
    </div>
  );
}

/**
 * Second-act after hard-block proof: violations + journals as consequence of broken rules.
 * Keeps enforcement as the spine — not a second product pitch.
 */
function WorkflowProofSection({ data, openImageViewer }) {
  const beats = (data?.beats || []).filter((beat) => beat?.visual?.src);
  if (!beats.length && !data?.headline) return null;

  return (
    <section id="workflow-proof" className="landing-section landing-workflow-proof">
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
        {data.headline && (
          <h2 className="landing-workflow-proof-headline">
            {Array.isArray(data.headlineLines) && data.headlineLines.length > 0 ? (
              data.headlineLines.map((line, i) => (
                <span key={i} className="landing-workflow-proof-headline-line">
                  <MarketingHeadlineAccent text={line} accent={data.headlineAccent} />
                </span>
              ))
            ) : (
              <MarketingResponsiveHeadline
                text={data.headline}
                mobileLines={data.headlineMobileLines}
                accent={data.headlineAccent}
              />
            )}
          </h2>
        )}
        {data.intro && <p className="landing-workflow-proof-intro">{data.intro}</p>}
        {beats.length > 0 && (
          <div className="landing-workflow-proof-beats">
            {beats.map((beat) => (
              <article key={beat.id || beat.visual.src} className="landing-workflow-proof-beat">
                <figure className="landing-workflow-proof-figure">
                  <div className="landing-workflow-proof-bezel">
                    <img
                      src={beat.visual.src}
                      alt={beat.visual.alt || ''}
                      className={openImageViewer ? 'landing-desk-shot' : undefined}
                      loading="lazy"
                      decoding="async"
                      role={openImageViewer ? 'button' : undefined}
                      tabIndex={openImageViewer ? 0 : undefined}
                      onClick={
                        openImageViewer
                          ? () => openImageViewer(beat.visual.src, beat.visual.alt || '')
                          : undefined
                      }
                      onKeyDown={
                        openImageViewer
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openImageViewer(beat.visual.src, beat.visual.alt || '');
                              }
                            }
                          : undefined
                      }
                    />
                  </div>
                </figure>
                <div className="landing-workflow-proof-copy">
                  {beat.kicker && (
                    <span className="landing-workflow-proof-kicker">{beat.kicker}</span>
                  )}
                  {beat.title && <h3 className="landing-workflow-proof-title">{beat.title}</h3>}
                  {beat.text && <p className="landing-workflow-proof-text">{beat.text}</p>}
                </div>
              </article>
            ))}
          </div>
        )}
        {data.aiNote && <p className="landing-workflow-proof-ai-note">{data.aiNote}</p>}
      </div>
    </section>
  );
}

function RuleDemoRulesPanel({ preview, breakActive }) {
  const panelRef = useRef(null);
  const [onScreen, setOnScreen] = useState(false);
  const rows = Array.isArray(preview?.rows)
    ? preview.rows.filter((row) => row?.label && row?.value)
    : [];

  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setOnScreen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { rootMargin: '80px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (!rows.length) return null;
  const title = preview.title || 'Trading Rules';
  const markedIndex = rows.findIndex((row) => row.illustratesBreak);
  const illustratedIndex = markedIndex >= 0 ? markedIndex : 0;
  const live = Boolean(breakActive) && onScreen;

  return (
    <div
      ref={panelRef}
      className={`landing-rule-demo-rules${breakActive ? ' landing-rule-demo-rules--break' : ''}${
        live ? ' landing-rule-demo-rules--live' : ''
      }`}
    >
      <div className="landing-rule-demo-rules__head">
        <span className="landing-rule-demo-rules__led" aria-hidden="true" />
        <p className="landing-rule-demo-rules__title">{title}</p>
        {preview.kicker ? (
          <span className="landing-rule-demo-rules__kicker">{preview.kicker}</span>
        ) : null}
      </div>
      <dl className="landing-rule-demo-rules__list">
        {rows.map((row, index) => {
          const showBreak = Boolean(breakActive) && index === illustratedIndex;
          return (
            <div
              key={row.label}
              className={`landing-rule-demo-rules__row${
                showBreak ? ' landing-rule-demo-rules__row--break' : ''
              }`}
            >
              <dt className="landing-rule-demo-rules__label">{row.label}</dt>
              <dd className="landing-rule-demo-rules__value">
                {row.value}
                {showBreak ? (
                  <span className="landing-rule-demo-rules__break-led" aria-hidden="true" />
                ) : null}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function RuleEnforcementDemoSection({ data }) {
  const callouts = data.callouts || [];
  const modeCards = data.modeCards || [];
  const hasSteps = callouts.length > 0;
  const rulesPreview = data.rulesPreview?.rows?.length ? data.rulesPreview : null;
  const ruleDemoImage = !rulesPreview && data.image?.src ? data.image : null;
  const defaultOpenId = callouts.find((item) => item.defaultExpanded)?.id ?? null;
  const [openStepId, setOpenStepId] = useState(defaultOpenId);

  const toggleStep = (id, title) => {
    setOpenStepId((current) => {
      if (current === id) return null;
      trackLandingExpandableExpanded({
        section: 'rule_enforcement',
        itemId: id,
        itemTitle: title,
      });
      return id;
    });
  };

  const stepProps = {
    openStepId,
    onToggleStep: toggleStep,
  };

  return (
    <section id="rule-enforcement" className="landing-section landing-rule-demo">
      <div className="landing-container">
        {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
        {data.headline && (
          <h2 className="landing-rule-demo-headline">
            <MarketingResponsiveHeadline
              text={data.headline}
              mobileLines={data.headlineMobileLines}
              accent={data.headlineAccent}
            />
          </h2>
        )}
        {(data.subheadline || data.featuresLinkHref) && (
          <p className="landing-rule-demo-subheadline">
            {data.subheadline}
            {data.subheadline && data.featuresLinkHref ? ' ' : null}
            <RuleDemoFeaturesLink data={data} />
          </p>
        )}
        <figure className="landing-rule-demo-figure">
          <div className="landing-rule-demo-stage">
            {hasSteps && (
              <RuleDemoCalloutsStack data={data} callouts={callouts} {...stepProps} />
            )}
            <div className="landing-rule-demo-visual-col">
              <div className="landing-rule-demo-visual">
                {rulesPreview ? (
                  <RuleDemoRulesPanel
                    preview={rulesPreview}
                    breakActive={openStepId === 'no-override'}
                  />
                ) : ruleDemoImage ? (
                  <img
                    src={ruleDemoImage.src}
                    alt={ruleDemoImage.alt || ''}
                    className="landing-rule-demo-image"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  renderMedia(data.visual, {
                    fill: false,
                    posterPreview: true,
                    posterPreviewOnMobile: true,
                  })
                )}
              </div>
              {data.caption && (
                <figcaption className="landing-rule-demo-caption landing-rule-demo-caption--under-image">
                  {data.caption}
                </figcaption>
              )}
            </div>
          </div>
          {hasSteps && (
            <RuleDemoCalloutsStack data={data} callouts={callouts} mobile {...stepProps} />
          )}
          <RuleDemoModesRow modeCards={modeCards} />
        </figure>
      </div>
    </section>
  );
}

function PainExecutionLoop({ loop }) {
  const beats = loop?.beats || [];
  if (!beats.length) return null;
  const hasBreak = Boolean(loop.breakTitle || loop.breakDetail);

  return (
    <div className="landing-pain-loop" aria-label="The trading discipline loop">
      <ol className="landing-pain-loop-timeline">
        {beats.map((beat, index) => {
          const isLast = index === beats.length - 1;
          return (
            <li
              key={beat.id || index}
              className={[
                'landing-pain-loop-step',
                beat.loop ? 'landing-pain-loop-step--loop' : '',
                isLast ? 'landing-pain-loop-step--last' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="landing-pain-loop-rail" aria-hidden="true">
                <span className="landing-pain-loop-node">{index + 1}</span>
                {!isLast && <span className="landing-pain-loop-line" />}
                {!isLast && (
                  <span className="landing-pain-loop-arrow">
                    <ArrowDown size={14} />
                  </span>
                )}
              </div>
              <div className="landing-pain-loop-step-body">
                <div className="landing-pain-loop-step-heading">
                  <h3 className="landing-pain-loop-step-title">{beat.title}</h3>
                  {beat.loop && (
                    <span
                      className="landing-pain-loop-infinity"
                      aria-hidden="true"
                      title="The loop continues"
                    />
                  )}
                </div>
                {beat.detail && (
                  <p className="landing-pain-loop-step-detail">{beat.detail}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {hasBreak && (
        <>
          <div className="landing-pain-loop-to-break" aria-hidden="true">
            <span className="landing-pain-loop-to-break-line" />
            <span className="landing-pain-loop-to-break-arrow">
              <ArrowDown size={14} />
            </span>
          </div>
          <div className="landing-pain-loop-break">
            {loop.breakLabel && (
              <span className="landing-pain-loop-break-label">{loop.breakLabel}</span>
            )}
            <p className="landing-pain-loop-break-text">
              <MarketingHeadlineAccent
                text={[loop.breakTitle, loop.breakDetail].filter(Boolean).join(' ')}
                accent={loop.breakAccent || 'PrecisionTrader'}
              />
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export const sectionRenderers = {
  /** Quote cards after hero; data.eyebrow?, data.title?, data.quotes: [{ id?, text, subtext? }] */
  socialProofBanner: (data) =>
    SocialProofBannerSection(
      {
        ...data,
        quotes: data.quotes?.slice(0, 3),
      },
      {
        sectionClassName: data.layout === 'centered' ? 'landing-social-proof-banner--centered' : '',
      }
    ),

  ruleEnforcementDemo: (data) => <RuleEnforcementDemoSection data={data} />,

  workflowProof: (data, { openImageViewer }) => (
    <WorkflowProofSection data={data} openImageViewer={openImageViewer} />
  ),

  quickAnswers: (data) => <QuickAnswersSection data={data} />,
  faq: (data) => <QuickAnswersSection data={data} variant="faq" />,

  comparisonTable: (data) => <ComparisonTableSection data={data} />,

  startPath: (data) => {
    const steps = data.steps || [];
    if (!steps.length) return null;
    const proBrokers = getHomeStartPathBrokersForDisplay();
    const publicUrl = process.env.PUBLIC_URL || '';
    const proMoreText = data.proBrokersMoreText || 'and more';
    const proHref = data.proBrokersHref || '/brokers';

    return (
      <section
        id={data.id || 'start-path'}
        className="landing-section landing-start-path"
      >
        <div className="landing-container">
          <div className="landing-start-path-header">
            {data.eyebrow && (
              <p className="landing-section-eyebrow">{data.eyebrow}</p>
            )}
            {data.headline && (
              <h2 className="landing-start-path-headline">
                <MarketingResponsiveHeadline
                  text={data.headline}
                  mobileLines={data.headlineMobileLines}
                  accent={data.headlineAccent}
                />
              </h2>
            )}
            {data.intro && (
              <p className="landing-start-path-intro">{data.intro}</p>
            )}
          </div>
          <ol className="landing-start-path-steps">
            {steps.map((step, i) => (
              <li key={step.id || i} className="landing-start-path-step">
                <span className="landing-start-path-step-index" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="landing-start-path-step-title">{step.title}</h3>
                {step.text && (
                  <p className="landing-start-path-step-text">{step.text}</p>
                )}
              </li>
            ))}
          </ol>
          {proBrokers.length > 0 && (
            <div className="landing-start-path-pro-brokers">
              <ul className="landing-start-path-pro-broker-row">
                {proBrokers.map((broker) => {
                  const fit = broker.logoFit === 'contain' ? 'contain' : 'cover';
                  const wrapClass = [
                    'landing-start-path-pro-broker-logo',
                    fit === 'contain' ? 'landing-start-path-pro-broker-logo--contain' : '',
                    broker.logoBg === 'dark' ? 'landing-start-path-pro-broker-logo--dark' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <li key={broker.id} className="landing-start-path-pro-broker">
                      <span className={wrapClass}>
                        <img
                          src={`${publicUrl}${broker.logoSrc}`}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      </span>
                      <span className="landing-start-path-pro-broker-name">{broker.name}</span>
                    </li>
                  );
                })}
                <li className="landing-start-path-pro-broker landing-start-path-pro-broker--more">
                  <Link to={proHref} className="landing-start-path-pro-brokers-more">
                    {proMoreText}
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </section>
    );
  },

  trustExplainer: (data) => <TrustExplainerSection data={data} />,

  dataSecurityPolicy: (data) => <TrustExplainerSection data={data} />,

  screenshotsCarousel: (data) => <ScreenshotsCarouselSection data={data} />,

  hero: (data, { CtaButton, openImageViewer, landingConfigId }) => {
    const heroVisuals = Array.isArray(data.visuals) ? data.visuals.filter((x) => x?.src) : [];
    const singleFromVisuals = heroVisuals.length === 1 ? heroVisuals[0] : null;
    const showHeroCarousel = heroVisuals.length > 1;
    const heroMediaOpts = {
      fill: false,
      onImageClick: openImageViewer,
      posterPreview: true,
      posterPreviewOnMobile: true,
      analyticsContext: landingConfigId ? { landing_config_id: landingConfigId } : undefined,
    };
    const heroMedia = showHeroCarousel ? (
      <LandingRotatingMediaCarousel
        visuals={heroVisuals}
        intervalMs={data.heroCarouselIntervalMs ?? 7000}
        onImageClick={openImageViewer}
        ui={HERO_CAROUSEL_UI}
      />
    ) : singleFromVisuals ? (
      renderMedia(singleFromVisuals, heroMediaOpts)
    ) : (
      renderMedia(data.visual, heroMediaOpts)
    );

    return (
      <section className="landing-hero landing-hero-stacked">
        <div className="landing-hero-inner">
          <div className="landing-hero-text">
            {data.categoryLabel && (
              <span className="landing-hero-category">{data.categoryLabel}</span>
            )}
            {data.typingAnimation ? (
              <h1 className="landing-hero-title landing-hero-title--rotating">
                <TypingAnimation
                  staticPrefix={data.typingAnimation.staticPrefix}
                  rotatingPhrases={data.typingAnimation.rotatingPhrases}
                  pauseDuration={data.typingAnimation.pauseDuration}
                />
                {data.typingAnimation.secondLine ? (
                  <>
                    <br />
                    <span className="landing-hero-title-line">
                      {data.typingAnimation.secondLine}
                    </span>
                  </>
                ) : null}
              </h1>
            ) : (
              <h1 className="landing-hero-title">
                {Array.isArray(data.headline)
                  ? data.headline.map((line, i) => (
                      <span key={i} className="landing-hero-title-line">
                        <MarketingHeadlineAccent
                          text={line}
                          accent={resolveMarketingAccent(data, i)}
                        />
                      </span>
                    ))
                  : (
                    <MarketingResponsiveHeadline
                      text={data.headline}
                      mobileLines={data.headlineMobileLines}
                      accent={resolveMarketingAccent(data)}
                    />
                  )}
              </h1>
            )}
            {data.paragraphs ? (
              data.paragraphs.map((p, i) => (
                <p key={i} className="landing-hero-subtitle">{p}</p>
              ))
            ) : (
              data.subheadline && <p className="landing-hero-subtitle">{data.subheadline}</p>
            )}
            <div className="landing-hero-actions">
              <CtaButton section="hero" />
              {data.secondaryCtaHref && data.secondaryCtaText && (
                <a
                  href={data.secondaryCtaHref}
                  className="landing-cta-btn landing-cta-btn--secondary"
                  onClick={() => {
                    analyticsService.trackTradingEvent?.('cta_button_clicked', {
                      variant: 'landing-hero-secondary',
                    });
                  }}
                >
                  {data.secondaryCtaText}
                </a>
              )}
            </div>
            {data.honestyNote && (
              <p className="landing-honesty-note landing-honesty-note--hero landing-honesty-note--hero-inline">{data.honestyNote}</p>
            )}
            {data.honestyNoteSecondary && (
              <p className="landing-honesty-note landing-honesty-note--hero landing-honesty-note--hero-inline landing-honesty-note--secondary">{data.honestyNoteSecondary}</p>
            )}
            {data.featurePillsLabel && Array.isArray(data.featurePills) && data.featurePills.length > 0 && (
              <div className="landing-hero-feature-pills">
                <p className="landing-hero-feature-pills__label">
                  {data.featurePillsLabel}
                </p>
                <nav
                  className="landing-hero-feature-pills__list"
                  aria-label="Product features"
                >
                  {data.featurePills.map((pill) => {
                    const href = pill.href || '/features';
                    const featured = Boolean(pill.featured);
                    const className = `landing-hero-feature-pill${featured ? ' landing-hero-feature-pill--featured' : ''}`;
                    const content = (
                      <>
                        {featured ? (
                          <span className="landing-hero-feature-pill__dot" aria-hidden="true" />
                        ) : null}
                        {pill.label}
                      </>
                    );
                    if (href.startsWith('#')) {
                      return (
                        <a key={pill.label} href={href} className={className}>
                          {content}
                        </a>
                      );
                    }
                    return (
                      <Link key={pill.label} to={href} className={className}>
                        {content}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}
          </div>
          <div
            className={`landing-hero-visual${showHeroCarousel ? ' landing-hero-visual--carousel' : ''}`}
          >
            <div className="landing-hero-media">{heroMedia}</div>
          </div>
          {data.honestyNote && (
            <p className="landing-honesty-note landing-honesty-note--hero landing-honesty-note--hero-below-visual">{data.honestyNote}</p>
          )}
          {data.honestyNoteSecondary && (
            <p className="landing-honesty-note landing-honesty-note--hero landing-honesty-note--hero-below-visual landing-honesty-note--secondary">{data.honestyNoteSecondary}</p>
          )}
        </div>
      </section>
    );
  },

  problemUsp: (data, { openImageViewer }) => (
    <ProblemUspSection data={data} openImageViewer={openImageViewer} />
  ),

  painAgitation: (data, { openImageViewer } = {}) => {
    const collageVisuals = Array.isArray(data.visuals)
      ? data.visuals.filter((v) => v?.src)
      : [];
    const hasCollage = collageVisuals.length >= 2;
    const singleVisual = !hasCollage && data.visual?.src ? data.visual : null;
    const hasVisual = hasCollage || Boolean(singleVisual);

    return (
      <section className="landing-section landing-pain-agitation">
        <div className="landing-container">
          {data.kicker && <p className="landing-pain-kicker">{data.kicker}</p>}
          <div
            className={`landing-pain-layout${hasVisual ? ' landing-pain-layout--with-visual' : ''}`}
          >
            {(data.headline || data.subheadline) && (
              <div className="landing-pain-header">
                {data.headline && (
                  <h2 className="landing-pain-headline">
                    <MarketingResponsiveHeadline
                      text={data.headline}
                      mobileLines={data.headlineMobileLines}
                      accent={resolveMarketingAccent(data)}
                    />
                  </h2>
                )}
                {data.subheadline && (
                  <p className="landing-pain-subheadline">{data.subheadline}</p>
                )}
              </div>
            )}
            {hasCollage && (
              <PainVisualCollage visuals={collageVisuals} openImageViewer={openImageViewer} />
            )}
            {singleVisual && (
              <div className="landing-pain-visual">
                <img
                  src={singleVisual.src}
                  alt={singleVisual.alt || ''}
                  className="landing-pain-visual-image"
                  loading="lazy"
                  decoding="async"
                  onClick={
                    openImageViewer
                      ? () => openImageViewer(singleVisual.src, singleVisual.alt || '')
                      : undefined
                  }
                  style={openImageViewer ? { cursor: 'pointer' } : undefined}
                />
              </div>
            )}
            <div className="landing-pain-copy">
              {data.executionLoop?.beats?.length > 0 && (
                <PainExecutionLoop loop={data.executionLoop} />
              )}
              {data.bullets?.length > 0 && (
                <ul className="landing-pain-bullets">
                  {data.bullets.map((bullet, i) => (
                    <li key={i}>
                      <MarketingHeadlineAccent
                        text={bullet}
                        accent={data.bulletAccents?.[i]}
                        className="marketing-headline-accent"
                      />
                    </li>
                  ))}
                </ul>
              )}
              {data.stat && <p className="landing-pain-stat">{data.stat}</p>}
            </div>
          </div>
        </div>
      </section>
    );
  },

  mechanism: (data, { openImageViewer }) => {
    const pillarIcons = {
      'rules-setup-enforcement': ShieldLock,
      'rule-setup': TradingRulesShieldIcon,
      'rule-enforcement': Lock,
      'trading-workflow': TradingWorkflowIcon,
      'pattern-recognition': PrecisionAIIcon,
    };

    return (
      <section className="landing-section landing-mechanism">
        <div className="landing-container">
          {data.eyebrow && <p className="landing-section-eyebrow">{data.eyebrow}</p>}
          {data.headline && (
            <h2 className="landing-mechanism-headline">
              <MarketingResponsiveHeadline
                text={data.headline}
                mobileLines={data.headlineMobileLines}
                accent={data.headlineAccent}
              />
            </h2>
          )}
          {data.intro && <p className="landing-mechanism-intro">{data.intro}</p>}
          <div className="landing-mechanism-stage">
            {data.showcaseImage && (
              <MechanismShowcaseImage image={data.showcaseImage} onImageClick={openImageViewer} />
            )}
            <MechanismPillarsList
              pillars={data.pillars}
              pillarIcons={pillarIcons}
              openImageViewer={openImageViewer}
            />
          </div>
        </div>
      </section>
    );
  },

  secondaryCta: (data, { CtaButton, openImageViewer }) => {
    const visual = data.visual?.src ? data.visual : data.visuals?.find((v) => v?.src) || null;
    const hasVisual = Boolean(visual?.src);

    return (
      <section
        className={`landing-section landing-secondary-cta${hasVisual ? ' landing-secondary-cta--has-visual' : ''}`}
      >
        <div className="landing-secondary-cta-chart" aria-hidden="true">
          <MockCandlestickBackdrop
            upColor="#07CBA3"
            downColor="#F73263"
            widthFraction={0.67}
            backgroundColor="#07090e"
            gridColor="rgba(232, 237, 244, 0.07)"
            textColor="rgba(168, 180, 200, 0.5)"
          />
        </div>
        <div className="landing-container landing-secondary-cta-inner">
          {(data.processLine || data.closingLines?.length > 0 || data.closingLine) && (
            <div className="landing-secondary-cta-header">
              {data.processLine && (
                <p className="landing-secondary-cta-process">
                  {Array.isArray(data.processLineLines) && data.processLineLines.length > 0 ? (
                    data.processLineLines.map((line, i) => (
                      <span key={i} className="landing-secondary-cta-process-line">
                        <MarketingHeadlineAccent
                          text={line}
                          accent={data.processLineAccent ?? resolveMarketingAccent(data)}
                        />
                      </span>
                    ))
                  ) : (
                    <MarketingResponsiveHeadline
                      text={data.processLine}
                      mobileLines={data.processLineMobileLines}
                      accent={data.processLineAccent ?? resolveMarketingAccent(data)}
                    />
                  )}
                </p>
              )}
              {data.closingLines?.length > 0 ? (
                <div className="landing-secondary-cta-closing">
                  {data.closingLines.map((line, i) => (
                    <p key={i} className="landing-secondary-cta-closing-line">
                      <MarketingHeadlineAccent
                        text={line}
                        accent={resolveClosingLineAccent(data, i) ?? resolveMarketingAccent(data, i)}
                      />
                    </p>
                  ))}
                </div>
              ) : (
                data.closingLine && (
                  <p className="landing-secondary-cta-closing-line">{data.closingLine}</p>
                )
              )}
            </div>
          )}
          <div className="landing-secondary-cta-body">
            <div className="landing-secondary-cta-copy">
              <CtaButton section="secondary" large />
              {data.trialText && <p className="landing-secondary-cta-trial">{data.trialText}</p>}
              {data.honestyNote && (
                <p className="landing-honesty-note landing-honesty-note--cta">{data.honestyNote}</p>
              )}
            </div>
            {hasVisual && (
              <figure className="landing-secondary-cta-visual">
                <img
                  src={visual.src}
                  alt={visual.alt || ''}
                  loading="lazy"
                  decoding="async"
                  onClick={
                    openImageViewer
                      ? () => openImageViewer(visual.src, visual.alt || '')
                      : undefined
                  }
                  style={openImageViewer ? { cursor: 'pointer' } : undefined}
                />
              </figure>
            )}
          </div>
        </div>
      </section>
    );
  },

  appendix: (data) => (
    <section className="landing-section landing-appendix">
      <div className="landing-container">
        {data.title && (
          <h2 className="landing-appendix-title">
            <MarketingHeadlineAccent text={data.title} accent={resolveMarketingAccent(data)} />
          </h2>
        )}
        {data.intro && <p className="landing-appendix-intro">{data.intro}</p>}
        {data.items?.length > 0 && (
          <ul className="landing-appendix-grid">
            {data.items.map((item, i) => (
              <li key={item.title || i} className="landing-appendix-item">
                <h3 className="landing-appendix-item-title">{item.title}</h3>
                {item.description && <p className="landing-appendix-item-description">{item.description}</p>}
                {item.learnMoreHref && (
                  <Link to={item.learnMoreHref} className="landing-appendix-item-link">
                    {item.learnMoreText || 'Learn more'}
                  </Link>
                )}
                {item.icon && (
                  <span className="landing-appendix-item-icon" aria-hidden="true">
                    <LandingAppendixIcon iconId={item.icon} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {data.featuresLinkHref && (
          <p className="landing-appendix-link-wrap">
            <Link to={data.featuresLinkHref} className="landing-appendix-link">
              {data.featuresLinkText || 'See all features'}
            </Link>
          </p>
        )}
      </div>
    </section>
  ),

  benefits: (data, helpers) => {
    const useStackedTabs = data.featureTabs?.length > 0;

    return (
      <section className="landing-section landing-benefits">
        <div className="landing-benefits-inner">
        <h2
          className={`landing-section-title${
            data.titleAccent ? ' landing-section-title--accent' : ''
          }`}
        >
          {Array.isArray(data.title)
            ? data.title.map((line, i) => (
                <span key={i} className="landing-section-title-line">
                  {line}
                </span>
              ))
            : data.title}
        </h2>
        {data.steps?.length > 0 ? (
          <ol
            className={`landing-benefits-steps${
              data.stepsVariant === 'numbered' ? ' landing-benefits-steps--numbered' : ''
            }`}
          >
            {data.steps.map((step, i) => {
              const isNumbered = data.stepsVariant === 'numbered';
              const isPink =
                !isNumbered &&
                (step.phaseColor === 'pink' || step.phase?.toLowerCase() === 'review');
              const phaseLabel = isNumbered ? String(i + 1) : step.phase;
              return (
                <li key={i} className="landing-benefits-step">
                  <span
                    className={`landing-benefits-step-phase${
                      isNumbered ? ' landing-benefits-step-phase--pill' : ''
                    }${!isNumbered && isPink ? ' landing-benefits-step-phase--pink' : ''}`}
                  >
                    {phaseLabel}
                  </span>
                  <span
                    className="landing-benefits-step-text"
                    dangerouslySetInnerHTML={{ __html: step.text }}
                  />
                </li>
              );
            })}
          </ol>
        ) : data.introText ? (
          <p className="landing-benefits-intro">{data.introText}</p>
        ) : null}

        {useStackedTabs ? (
          <div className="landing-benefits-stacked">
            <FeatureTabTray
              tabs={data.featureTabs}
              onImageClick={helpers?.openImageViewer}
            />
            <div className="landing-benefits-expandable-col">
              {data.items?.map((item, i) => (
                <ExpandableItem
                  key={i}
                  title={item.title}
                  content={item.description}
                  titleIcon={item.titleIcon}
                  analyticsSection="benefits"
                  analyticsId={item.id || item.title}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className={`landing-benefits-two-col ${
              data.layout === 'vertical' ? 'landing-benefits-vertical' : ''
            }${
              data.layout === 'visual-left-text-right'
                ? ' landing-benefits-two-col--visual-emphasis'
                : ''
            }`}
          >
            <div
              className={`landing-benefits-visual${
                Array.isArray(data.visuals) && data.visuals.length > 0
                  ? ' landing-benefits-visual--carousel'
                  : ''
              }`}
            >
              {Array.isArray(data.visuals) && data.visuals.length > 0 ? (
                <BenefitsCarousel
                  visuals={data.visuals}
                  onImageClick={helpers?.openImageViewer}
                />
              ) : (
                renderMedia(data.visual, { onImageClick: helpers?.openImageViewer })
              )}
            </div>
            <div className="landing-benefits-list landing-benefits-expandable">
              {data.items?.map((item, i) => (
                <ExpandableItem
                  key={i}
                  title={item.title}
                  content={item.description}
                  analyticsSection="benefits"
                  analyticsId={item.id || item.title}
                />
              ))}
            </div>
          </div>
        )}
        </div>
      </section>
    );
  },

  ctaContrasting: (data, { ctaUrl, ctaLabel, handleCtaClick }) => (
    <section className="landing-section landing-cta-contrasting">
      <div
        className={`landing-cta-contrasting-inner${data.usageSignals?.items?.length ? ' landing-cta-contrasting-inner--wide' : ''}`}
      >
        {((typeof data.headline === 'string' && data.headline.trim()) ||
          (Array.isArray(data.headline) && data.headline.length > 0)) && (
          <h2 className="landing-cta-contrasting-headline">
            {Array.isArray(data.headline)
              ? data.headline.map((line, i) => (
                  <span key={i} className="landing-cta-contrasting-headline-line">
                    {line}
                  </span>
                ))
              : data.headline}
          </h2>
        )}
        {data.subheadline && (
          <p className="landing-cta-contrasting-sub">{data.subheadline}</p>
        )}
        <Link
          to={ctaUrl}
          className="landing-cta-contrasting-btn"
          onClick={() => handleCtaClick('contrasting')}
        >
          {data.ctaText || ctaLabel}
        </Link>
        {data.trialNote && (
          <p className="landing-cta-trial-note">{data.trialNote}</p>
        )}
        <UsageSignalsBlock usageSignals={data.usageSignals} />
      </div>
    </section>
  ),

  features: (data, helpers) => (
    <section className="landing-section landing-features">
      <div className="landing-features-inner">
        <h2 className="landing-section-title">{data.title}</h2>
        {(data.introParagraphs || data.introText) && (
          <div className="landing-features-intro">
            {data.introParagraphs
              ? data.introParagraphs.map((p, i) => <p key={i} className="landing-features-intro-p">{p}</p>)
              : <p>{data.introText}</p>}
          </div>
        )}
        {data.showcaseVisual && (
          <div className="landing-features-showcase">
            {renderMedia(data.showcaseVisual, { onImageClick: helpers?.openImageViewer })}
          </div>
        )}
        <div className="landing-features-expandable">
          {data.items?.map((item, i) => (
            <ExpandableItem
              key={i}
              title={item.title}
              content={item.description}
              analyticsSection="features"
              analyticsId={item.id || item.title}
            />
          ))}
        </div>
      </div>
    </section>
  ),

  socialProof: (data, helpers) => (
    <section className="landing-section landing-social-proof">
      <div className="landing-social-proof-inner">
        <div className="landing-social-proof-two-col">
          {data.visual?.src && (
            <div className="landing-social-proof-visual">
              <img
                src={data.visual.src}
                alt={data.visual.alt || ''}
                onClick={helpers?.openImageViewer ? () => helpers.openImageViewer(data.visual.src, data.visual.alt || '') : undefined}
                style={helpers?.openImageViewer ? { cursor: 'pointer' } : undefined}
              />
            </div>
          )}
          <div className="landing-social-proof-content">
            <h2 className="landing-section-title">{data.title || 'What PrecisionTrader Is'}</h2>
            <ul className="landing-trust-signals">
              {data.trustSignals?.map((signal, i) => (
                <li key={i}>{signal}</li>
              ))}
            </ul>
            {data.platformType && (
              <p className="landing-platform-type">{data.platformType}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  ),

  supportedBrokers: (data, { landingConfigId }) => (
    <section className="landing-section landing-supported-brokers">
      <div className="landing-supported-brokers-outer">
        <SupportedBrokersBlock data={data} landingConfigId={landingConfigId} />
      </div>
    </section>
  ),

  ctaFinal: (data, { CtaButton, openImageViewer }) => (
    <section className={`landing-section landing-cta-final ${data.visual?.src ? 'landing-cta-final-has-visual' : ''}`}>
      <div className="landing-cta-final-inner">
        {data.visual?.src ? (
          <>
            <div className="landing-cta-final-content">
              <h2 className="landing-cta-final-title">
                {Array.isArray(data.title)
                  ? data.title.map((line, i) => (
                      <span key={i} className="landing-cta-final-title-line">
                        {line}
                      </span>
                    ))
                  : data.title || 'Focus on execution'}
              </h2>
              {data.paragraphs?.map((p, i) => (
                <p key={i} className="landing-cta-final-text">{p}</p>
              ))}
              <CtaButton section="final" large />
              {data.trialText && (
                <p className="landing-cta-trial">{data.trialText}</p>
              )}
              {data.pricingNote && (
                <p className="landing-pricing-note">
                  {Array.isArray(data.pricingNote)
                    ? data.pricingNote.map((line, i) => <span key={i}>{line}{i < data.pricingNote.length - 1 && <br />}</span>)
                    : data.pricingNote}
                </p>
              )}
            </div>
            <div className="landing-cta-final-visual">
              <div className="landing-cta-final-visual-inner">
                <img
                  src={data.visual.src}
                  alt={data.visual.alt || ''}
                  onClick={openImageViewer ? () => openImageViewer(data.visual.src, data.visual.alt || '') : undefined}
                  style={openImageViewer ? { cursor: 'pointer' } : undefined}
                />
                <div className="landing-cta-final-fade" aria-hidden="true" />
              </div>
            </div>
          </>
        ) : (
          <>
            <h2 className="landing-cta-final-title">
              {Array.isArray(data.title)
                ? data.title.map((line, i) => (
                    <span key={i} className="landing-cta-final-title-line">
                      {line}
                    </span>
                  ))
                : data.title || 'Focus on execution'}
            </h2>
            {data.paragraphs?.map((p, i) => (
              <p key={i} className="landing-cta-final-text">{p}</p>
            ))}
            <CtaButton section="final" large />
            {data.trialText && (
              <p className="landing-cta-trial">{data.trialText}</p>
            )}
            {data.pricingNote && (
              <p className="landing-pricing-note">
                {Array.isArray(data.pricingNote)
                  ? data.pricingNote.map((line, i) => <span key={i}>{line}{i < data.pricingNote.length - 1 && <br />}</span>)
                  : data.pricingNote}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  ),
};

/** Default section order when not specified in config */
export const defaultSectionOrder = [
  'hero',
  'mechanism',
  'ruleEnforcementDemo',
  'socialProofBanner',
  'comparisonTable',
  'workflowProof',
  'startPath',
  'secondaryCta',
  'faq',
];
